import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, updateDoc, getDoc, setDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import Icon from '../components/shared/Icon';

export default function AcceptInvite() {
  const { inviteToken } = useParams();
  const { user, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!inviteToken) {
      navigate('/login');
      return;
    }

    // Check if invite exists and is valid (requires auth)
    const checkInvite = async () => {
      // If user is not signed in, we'll show the sign-in button
      // and check the invite after they sign in
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const q = query(
          collection(db, 'invites'),
          where('token', '==', inviteToken)
        );
        const snapshot = await getDocs(q);
        
        if (snapshot.empty) {
          toast.error('Invalid invite link');
          navigate('/login');
          return;
        }

        const inviteData = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
        
        if (inviteData.used) {
          toast.error('This invite has already been used');
          navigate('/login');
          return;
        }

        setInvite(inviteData);
      } catch (error) {
        console.error('Error checking invite:', error);
        toast.error('Error loading invite');
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    checkInvite();
  }, [inviteToken, navigate, user]);

  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
      // After sign in, useEffect will check the invite
    } catch (error) {
      console.error('Error signing in:', error);
      toast.error('Failed to sign in. Please try again.');
    }
  };

  const handleAcceptInvite = async () => {
    if (!user) {
      await handleSignIn();
      return;
    }

    // If invite not loaded yet, try to load it
    let inviteToUse = invite;
    if (!inviteToUse) {
      try {
        const q = query(
          collection(db, 'invites'),
          where('token', '==', inviteToken)
        );
        const snapshot = await getDocs(q);
        if (snapshot.empty) {
          toast.error('Invalid invite link');
          return;
        }
        const inviteData = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
        if (inviteData.used) {
          toast.error('This invite has already been used');
          return;
        }
        inviteToUse = inviteData;
        setInvite(inviteData);
      } catch (error) {
        console.error('Error loading invite:', error);
        toast.error('Error loading invite');
        return;
      }
    }

    if (!inviteToUse) return;

    setProcessing(true);

    try {
      // Check if user already has a family
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();

      if (userData?.familyId) {
        if (userData.familyId === inviteToUse.familyId) {
          toast.success('You\'re already part of this family!');
          navigate('/');
          return;
        } else {
          toast.error('You\'re already part of another family');
          navigate('/');
          return;
        }
      }

      // Get family document
      const familyDoc = await getDoc(doc(db, 'families', inviteToUse.familyId));
      if (!familyDoc.exists()) {
        toast.error('Family not found');
        navigate('/login');
        return;
      }

      const familyData = familyDoc.data();

      // Add user to family members
      if (!familyData.members.includes(user.uid)) {
        await updateDoc(doc(db, 'families', inviteToUse.familyId), {
          members: arrayUnion(user.uid),
        });
      }

      // Update user document with familyId
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        displayName: user.displayName || '',
        familyId: inviteToUse.familyId,
        onboardingComplete: true, // Skip onboarding if joining via invite
        createdAt: userDoc.exists() ? userData.createdAt : new Date(),
      }, { merge: true });

      // Mark invite as used
      await updateDoc(doc(db, 'invites', inviteToUse.id), {
        used: true,
        usedBy: user.uid,
        usedAt: new Date(),
      });

      toast.success('Welcome to the family!');
      navigate('/');
    } catch (error) {
      console.error('Error accepting invite:', error);
      toast.error('Failed to accept invite. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-white flex items-center justify-center">
        <div className="text-center">
          <div className="mb-4 animate-bounce">
            <img 
              src="/logo-nobg.png" 
              alt="Loading" 
              className="w-16 h-auto mx-auto object-contain"
              style={{ aspectRatio: '2 / 1' }}
            />
          </div>
          <div className="text-lg text-text-secondary">Loading invite...</div>
        </div>
      </div>
    );
  }

  if (!invite) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-card shadow-card p-8">
        <div className="text-center mb-8">
          <div className="mb-4"><Icon name="👥" size="4x" className="text-primary" /></div>
          <h1 className="font-sans text-2xl sm:text-3xl font-semibold text-text-primary mb-3 tracking-tight">
            You've Been Invited!
          </h1>
          <p className="text-text-secondary">
            {user ? (
              <>
                You're being invited to join <strong>{invite.email}</strong>'s family on the Jewish Life Platform.
              </>
            ) : (
              <>
                Sign in to join <strong>{invite.email}</strong>'s family and start planning together.
              </>
            )}
          </p>
        </div>

        <div className="space-y-4">
          {!user ? (
            <button
              onClick={handleAcceptInvite}
              className="w-full bg-primary text-white font-semibold py-3 px-6 rounded-xl shadow-button hover:shadow-button-hover hover:bg-primary-dark transition-all"
            >
              Sign In with Google to Accept
            </button>
          ) : (
            <button
              onClick={handleAcceptInvite}
              disabled={processing}
              className="w-full bg-primary text-white font-semibold py-3 px-6 rounded-xl shadow-button hover:shadow-button-hover hover:bg-primary-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processing ? 'Joining Family...' : 'Accept Invite & Join Family'}
            </button>
          )}

          <button
            onClick={() => navigate('/login')}
            className="w-full bg-white border border-border text-text-secondary font-semibold py-3 px-6 rounded-xl hover:border-primary hover:text-primary transition-all"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

